import { useMutation } from 'react-query';
import { useNavigate } from 'react-router-dom';
import type { AxiosError } from 'axios';

import { useAppDispatch } from '@/Application/Hooks/Redux';
import { URL_ROUTES } from '@/constants';
import {
  useCencopaySuccess,
  useClearPaymentMethod
} from '@/modules/perseo-billing/hooks/index';
import { useMapOrderToConfirmation } from '@/modules/perseo-billing/hooks/order-confirmation/use-map-order-confirmation';
import useOnCreateIntentErrorInKiosco from '@/modules/perseo-billing/hooks/use-on-create-intent-error-in-kiosco';
import { useOnCreateIntentSuccessInKiosco } from '@/modules/perseo-billing/hooks/use-on-create-intent-success-in-kiosco';
import { usePaymentError } from '@/modules/perseo-billing/hooks/use-payment-error';
import { buildPurchaseSearchParams } from '@/modules/perseo-billing/hooks/utils';
import {
  type IPaymentRequest,
  PaymentProcessor
} from '@/modules/perseo-billing/interfaces/src/bff-web/billing/interfaces/payment-methods.interfaceV2';
import {
  postCreateOrderIntent,
  postCreatePurchaseOrder
} from '@/modules/perseo-billing/services/api/billing/mutations';
import { billingMutationKeys } from '@/modules/perseo-billing/services/api/billing/mutations/billing-mutation-factory';
import {
  setIsPaymentComplete,
  setPaymentSuccess
} from '@/modules/perseo-billing/store/slices/billing';
import { isDeviceKiosco } from '@/modules/perseo-billing/utils';
import type { IErrorResponse } from '@/modules/perseo-billing/utils/error-handler';
import { redirectToUrl } from '@/modules/perseo-billing/utils/window';

const useCreatePurchaseOrder = () => {
  const { handlePaymentError } = usePaymentError();

  const { clearPayment } = useClearPaymentMethod();

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { transformData } = useMapOrderToConfirmation();

  return useMutation(
    (params: IPaymentRequest) => postCreatePurchaseOrder(params),
    {
      mutationKey: billingMutationKeys.createPurchaseOrder,
      onSuccess(data) {
        dispatch(setIsPaymentComplete(true));
        const orderConfirmation = transformData(data);
        dispatch(setPaymentSuccess(orderConfirmation));
        const urlSearchParams = buildPurchaseSearchParams(
          orderConfirmation?.id
        );
        const paramsString = urlSearchParams.toString();
        const orderProcessingUrl = `${URL_ROUTES.BILLING_ORDER_PROCESSING_SUCCESS_PATH}?${paramsString}`;
        navigate(orderProcessingUrl, { replace: true });
        clearPayment();
        return;
      },
      onError(error: AxiosError<IErrorResponse>) {
        handlePaymentError(error?.response?.data);
        return;
      }
    }
  );
};

const useCreateOrderIntent = () => {
  const { handleCencoPaySuccess } = useCencopaySuccess();
  const { handleKioscoSuccess } = useOnCreateIntentSuccessInKiosco();
  const { handleKioscoError } = useOnCreateIntentErrorInKiosco();
  const { handlePaymentError } = usePaymentError();
  const { clearPayment } = useClearPaymentMethod();

  return useMutation(
    (params: IPaymentRequest) => postCreateOrderIntent(params),
    {
      mutationKey: billingMutationKeys.createOrderIntent,
      onSuccess(data, variables) {
        const isCencoPay = variables?.payments?.some(
          (payment) => payment.paymentProcessor === PaymentProcessor.CENCOPAY
        );
        const isKioscoPay = variables?.payments?.some(
          (payment) => payment.paymentProcessor === PaymentProcessor.PINPAD
        );

        clearPayment();

        if (isKioscoPay) {
          handleKioscoSuccess(data);
          return;
        }

        if (isCencoPay) {
          handleCencoPaySuccess(data);
        }

        return redirectToUrl(data?.redirectURI);
      },
      onError(error: AxiosError<IErrorResponse>) {
        const isKiosco = isDeviceKiosco();
        if (isKiosco) {
          handleKioscoError(error?.response?.data ?? null);
        }
        handlePaymentError(error?.response?.data);
        return;
      }
    }
  );
};

export { useCreateOrderIntent, useCreatePurchaseOrder };
